/**
 * Expense Type Selector Component
 *
 * A specialized select component for choosing Wyden expense types.
 * Shows expense type names, descriptions, and account numbers.
 */

import { createSelect } from './select.js';
import {
  getExpenseTypeOptions,
  getExpenseTypeByString,
  WYDEN_EXPENSE_TYPES,
} from '../constants/expense-types.js';
import type { SelectOptions, ValidationState } from '../types/form.js';

/**
 * Options for ExpenseTypeSelector component
 */
export interface ExpenseTypeSelectorOptions {
  /** Unique identifier for the element */
  id?: string;
  /** Name attribute for form submission */
  name?: string;
  /** Current selected expense type (string like "EDUCATION", "CATERING", etc.) */
  value?: string | null;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /** CSS class name(s) */
  className?: string;
  /** Current validation state */
  validationState?: ValidationState;
  /** Additional HTML attributes */
  attributes?: Record<string, string>;
  /** Change event handler - returns expense type string or null */
  onChange?: (expenseType: string | null, event: Event) => void;
  /** Focus event handler */
  onFocus?: (event: FocusEvent) => void;
  /** Blur event handler */
  onBlur?: (event: FocusEvent) => void;
}

/**
 * Create an expense type selector element
 */
export function createExpenseTypeSelector(options: ExpenseTypeSelectorOptions): HTMLSelectElement {
  const {
    id,
    name,
    value,
    disabled = false,
    required = false,
    className = '',
    validationState = 'default',
    attributes = {},
    onChange,
    onFocus,
    onBlur,
  } = options;

  // Build select options with account numbers and descriptions
  const selectOptions = getExpenseTypeOptions().map(option => {
    const expenseType = WYDEN_EXPENSE_TYPES[option.value];
    return {
      value: option.value, // String key like "EDUCATION"
      label: `${option.label} (${expenseType?.accountNumber ?? 'N/A'}) - ${option.description}`,
    };
  });

  // Create the select with a placeholder
  const selectConfig: SelectOptions = {
    options: selectOptions,
    value: value || '',
    placeholder: 'Select expense type...',
    disabled,
    required,
    className,
    validationState,
    attributes,
    onChange: (selectValue, event) => {
      if (onChange) {
        onChange(selectValue || null, event);
      }
    },
  };
  if (id) selectConfig.id = id;
  if (name) selectConfig.name = name;
  if (onFocus) selectConfig.onFocus = onFocus;
  if (onBlur) selectConfig.onBlur = onBlur;

  return createSelect(selectConfig);
}

/**
 * ExpenseTypeSelector class for object-oriented usage
 */
export class ExpenseTypeSelector {
  private element: HTMLSelectElement;
  private options: ExpenseTypeSelectorOptions;

  constructor(options: ExpenseTypeSelectorOptions) {
    this.options = { ...options };
    this.element = createExpenseTypeSelector(this.options);
  }

  /**
   * Get the select element
   */
  getElement(): HTMLSelectElement {
    return this.element;
  }

  /**
   * Get the current selected expense type string
   */
  getValue(): string | null {
    const value = this.element.value;
    return value || null;
  }

  /**
   * Set the selected expense type
   */
  setValue(expenseType: string | null): this {
    this.element.value = expenseType || '';
    this.options.value = expenseType;
    return this;
  }

  /**
   * Get the account number for the currently selected expense type
   */
  getAccountNumber(): string | null {
    const value = this.element.value;
    if (value) {
      const expenseType = getExpenseTypeByString(value);
      return expenseType?.accountNumber || null;
    }
    return null;
  }

  /**
   * Set the validation state
   */
  setValidationState(state: ValidationState): this {
    this.options.validationState = state;

    const stateClasses = {
      default: 'border-neutral-800 hover:border-neutral-700 focus:border-primary focus:ring-primary/20',
      error: 'border-destructive hover:border-destructive focus:border-destructive focus:ring-destructive/20',
      success: 'border-success hover:border-success focus:border-success focus:ring-success/20',
      warning: 'border-warning hover:border-warning focus:border-warning focus:ring-warning/20',
    };

    // Remove all state classes
    Object.values(stateClasses).forEach((classes) => {
      classes.split(' ').forEach((cls) => {
        this.element.classList.remove(cls);
      });
    });

    // Add new state classes
    stateClasses[state].split(' ').forEach((cls) => {
      this.element.classList.add(cls);
    });

    return this;
  }

  /**
   * Set disabled state
   */
  setDisabled(disabled: boolean): this {
    this.element.disabled = disabled;
    this.options.disabled = disabled;
    return this;
  }

  /**
   * Check if disabled
   */
  isDisabled(): boolean {
    return this.element.disabled;
  }

  /**
   * Focus the selector
   */
  focus(): this {
    this.element.focus();
    return this;
  }

  /**
   * Blur the selector
   */
  blur(): this {
    this.element.blur();
    return this;
  }

  /**
   * Check validity
   */
  checkValidity(): boolean {
    return this.element.checkValidity();
  }

  /**
   * Report validity (shows browser validation message)
   */
  reportValidity(): boolean {
    return this.element.reportValidity();
  }

  /**
   * Add event listener
   */
  on<K extends keyof HTMLElementEventMap>(
    type: K,
    listener: (event: HTMLElementEventMap[K]) => void
  ): this {
    this.element.addEventListener(type, listener);
    return this;
  }

  /**
   * Remove event listener
   */
  off<K extends keyof HTMLElementEventMap>(
    type: K,
    listener: (event: HTMLElementEventMap[K]) => void
  ): this {
    this.element.removeEventListener(type, listener);
    return this;
  }
}
